import React, { useEffect } from "react";
import Creatable from "react-select/creatable";
import { useField, useFormikContext } from "formik";
import { Form } from "react-bootstrap";
import type { ActionMeta } from "react-select";

type MultiValue<T> = readonly T[];
type SingleValue<T> = T | null;

interface CreatableOption {
  value: unknown;
  label: string;
  __isNew__?: boolean;
}

interface ItemCreatableProps {
  name: string;
  multi?: boolean;
  placeholder?: string;
  disabled?: boolean;
  options?: CreatableOption[];
  handleChange?: (value: CreatableOption | CreatableOption[] | null) => void;
  menuPlacement?: "auto" | "bottom" | "top";
}

const ItemCreatable: React.FC<ItemCreatableProps> = ({
  name,
  multi = false,
  placeholder = "",
  disabled = false,
  options = [],
  handleChange,
  menuPlacement = "auto",
}) => {
  const [field, meta] = useField(name);

  const { setFieldValue } = useFormikContext();

  useEffect(() => {
    if (handleChange) {
      handleChange(field.value);
    }
  }, [field.value, handleChange]);

  let formGroupClass = "";
  if (meta && meta.touched) {
    if (meta.error) {
      formGroupClass = "is-invalid";
    } else {
      formGroupClass = "is-valid";
    }
  }

  useEffect(() => {
    if (
      !options.find((ops) => field.value && ops.value === (field.value as CreatableOption).value) &&
      options[0] &&
      !multi
    ) {
      setFieldValue(
        name,
        options.find((ops) => ops.value === (meta.initialValue as CreatableOption)?.value) ||
          options[0]
      );
    }
  }, [options, field.value, meta.initialValue, multi, name, setFieldValue]);

  const getValue = (): CreatableOption | CreatableOption[] | null => {
    if (options.length > 0) {
      if (field.value) {
        if (multi) {
          const value = options.filter(
            (ops) => (field.value as CreatableOption[]).filter((f) => f.value === ops.value).length > 0
          );
          const new_values = (field.value as CreatableOption[]).filter((i) => i.__isNew__);
          new_values.forEach((v) => value.push(v));
          return value;
        } else {
          return options.find((ops) => ops.value === (field.value as CreatableOption).value) || null;
        }
      } else {
        return multi ? [] : null;
      }
    } else {
      if (field.value) {
        // show the current values, will be replaced when we get the options choice
        return field.value as CreatableOption | CreatableOption[];
      } else {
        return multi ? [] : null;
      }
    }
  };

  const setValue = (
    newValue: MultiValue<CreatableOption> | SingleValue<CreatableOption> | undefined,
    _actionMeta: ActionMeta<CreatableOption>
  ): void => {
    if (newValue === null || newValue === undefined) {
      setFieldValue(name, multi ? [] : null);
    } else {
      setFieldValue(name, newValue);
    }
  };

  return (
    <>
      <div translate="no">
        <Creatable
          className={`${formGroupClass}`}
          name={name}
          placeholder={placeholder}
          isMulti={multi}
          options={options}
          isDisabled={disabled}
          value={getValue()}
          onChange={setValue}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          menuPortalTarget={document.body}
          menuPlacement={menuPlacement}
        />
      </div>
      <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
    </>
  );
};

export default ItemCreatable;